import styled from 'styled-components';

import CircularProgress from 'components/shared/CircularProgress';
import TablePaginator from 'components/shared/table/TablePaginator';
import Workflow from 'models/Workflow';
import { Tab } from 'stores/inboundReferralsStore';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import ReferralCard from './ReferralCard';

// The inbox below the mobile breakpoint: a card per referral in place of the desktop table.
//
// Takes the same `workflows` array ControlledTable is given, so there is no second query and no
// second cache — the two renderers are two views of one fetch, and switching between them by
// resizing shows the same referrals in the same order.
//
// Pagination stays. Swapping to infinite scroll on mobile would be a product change rather than a
// responsiveness one, and it would also put the two renderers on different paging models.

type Props = {
  workflows: Workflow[];
  // Which tab's reading the cards should give — the card varies its fields per tab exactly as the
  // desktop columns do. See ReferralCard.
  tab: Tab;
  loading: boolean;
  pageCount: number;
  // 1-based, matching the store; TablePaginator works in 0-based indexes.
  page: number;
  setPage: (page: number) => void;
};

export default function MobileReferralList({ workflows, tab, loading, pageCount, page, setPage }: Props) {
  const pageIndex = page - 1;

  if (loading && workflows.length === 0) {
    return (
      <Centered>
        <CircularProgress />
      </Centered>
    );
  }

  if (workflows.length === 0) {
    return <Centered>No referrals found.</Centered>;
  }

  return (
    <Wrapper data-testid='mobile-referral-list'>
      <Cards>
        {workflows.map((workflow) => (
          <ReferralCard key={workflow.id} workflow={workflow} tab={tab} />
        ))}
      </Cards>
      <TablePaginator
        pageIndex={pageIndex}
        pageCount={pageCount}
        canPreviousPage={pageIndex > 0}
        canNextPage={pageIndex < pageCount - 1}
        // Clamped, because the paginator's jump-to-page box hands over whatever was typed: `0`
        // arrives as index -1 and would ask the api for page 0, leaving "Page 0 of N" with Previous
        // disabled and no way forward — the page's own correcting effect only pulls back pages that
        // are too HIGH. `pageCount || 1` keeps the upper bound sane before the first count lands.
        gotoPage={(index) => setPage(Math.min(Math.max(index, 0), Math.max(pageCount, 1) - 1) + 1)}
        nextPage={() => setPage(page + 1)}
        previousPage={() => setPage(page - 1)}
        // The paginator calls this alongside the navigation callback above, not instead of it, so
        // the page change is already handled there.
        onPageChange={() => {}}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Centered = styled(BodySmall)`
  display: flex;
  justify-content: center;
  padding: 48px 0;
  color: ${colors.black50};
`;
